import { type QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
    type ChangeDPSFundsPostRequest,
    type GetDPSDetailResponse,
    type GetPPDetailResponse,
    type GetZZJPreviewRequest,
    ProductType,
    type SendAuthenticationSMSRequest,
    type SetDPSTaxOptimumRequest,
} from "@repo/api";
import { type PensionContractType } from "@repo/logic";
import { usePrimaryAPIClient } from "../context/PrimaryAPIClient";
import { useQueryKeyFactory } from "../context/QueryKeyProvider";

export const useQuestions = () => {
    const { investorProfileApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    return useQuery({
        queryKey: createQueryKey({
            key: ["getQuestions"],
            includeUserIdentifier: false,
        }),
        queryFn: () => investorProfileApi.getInvestorProfileQuestionsGet(),
    });
};

type ZZJProps = {
    key: QueryKey;
    contractId: string;
    getZZJPreviewRequest: GetZZJPreviewRequest;
};

export const useZZJ = ({ key, contractId, getZZJPreviewRequest }: ZZJProps) => {
    const { contractsDpsApi } = usePrimaryAPIClient();

    return useQuery({
        queryKey: key,
        queryFn: () =>
            contractsDpsApi.previewChangeDPSFundsZZJPost({
                contractId,
                getZZJPreviewRequest,
            }),
    });
};

export const useSMS = ({ phoneNumber }: SendAuthenticationSMSRequest) => {
    const { authenticateApi } = usePrimaryAPIClient();

    const { data, isInitialLoading, refetch } = useQuery({
        queryKey: ["SendAuthenticationSMS.Post", phoneNumber],
        queryFn: () =>
            authenticateApi.sendAuthenticationSMSPost({
                sendAuthenticationSMSRequest: { phoneNumber },
            }),
        ...{ enabled: false }, // don't trigger on component mount
    });
    return { data, isInitialLoading, refetch };
};

export const useStrategy = ({ contractId, changeFundsRequest }: ChangeDPSFundsPostRequest) => {
    const { contractsDpsApi } = usePrimaryAPIClient();

    return useMutation({
        mutationFn: () => {
            return contractsDpsApi.changeDPSFundsPost({
                contractId,
                changeFundsRequest,
            });
        },
    });
};

type IUsePutTaxOptimum = {
    contractId: string;
    contractType: PensionContractType;
};

export const usePutTaxOptimum = ({ contractId, contractType }: IUsePutTaxOptimum) => {
    const { createQueryKey } = useQueryKeyFactory();
    const { contractsDpsApi, contractsPpApi } = usePrimaryAPIClient();
    const client = useQueryClient();
    const contractDetailQueryKey = createQueryKey({
        key: ["getContractDetail", contractId, contractType],
        includeUserIdentifier: false,
    });

    const mutationFn = async (taxOptimumRequest: SetDPSTaxOptimumRequest) => {
        if (contractType === ProductType.Uf) {
            await contractsDpsApi.setDPSTaxOptimumPut({
                contractId,
                setDPSTaxOptimumRequest: taxOptimumRequest,
            });
            return;
        }

        await contractsPpApi.setPPTaxOptimumPut({
            contractId,
            setPPTaxOptimumRequest: taxOptimumRequest,
        });
    };

    return useMutation({
        mutationFn,
        onSuccess: (_, variables) => {
            if (contractType === ProductType.Uf) {
                client.setQueryData<GetDPSDetailResponse>(contractDetailQueryKey, (data) => {
                    if (data === undefined) {
                        return;
                    }

                    return {
                        ...data,
                        contract: { ...data.contract, isTaxOptimum: variables.isActive },
                    };
                });
                return;
            }

            client.setQueryData<GetPPDetailResponse>(contractDetailQueryKey, (data) => {
                if (data === undefined) {
                    return;
                }
                return {
                    ...data,
                    contract: { ...data.contract, isTaxOptimum: variables.isActive },
                };
            });
        },
    });
};
